// Browser identifiers + Hybrid tracking: Browser Pixel (fbq) + server-side CAPI.
// Both sides share the SAME event_id so Meta deduplicates (no double counting).
import { getUTM } from "./utm";
import { devWarn } from "./logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FBP_KEY = "_m2s_fbp";
const FBC_KEY = "_m2s_fbc";
const EXTID_KEY = "_m2s_extid";
const PII_KEY = "_m2s_pii_v1";
const IP_KEY = "_m2s_cip_v1";          // cached client IP (IPv6 preferred)
const IP_TTL_MS = 60 * 60 * 1000;      // 1 hour

// Purchase must NEVER fire from the browser pixel — server-side CAPI only
// (better match quality for COD, and avoids double-count risk).
const SERVER_ONLY_EVENTS = ["Purchase"];

// Standard pixel parameters allowed in fbq custom data (internal stuff like `utm` is CAPI-only)
const PIXEL_DATA_KEYS = ["value", "currency", "content_ids", "content_name", "content_type", "contents", "num_items", "payment_method"];

function rand16() {
  return Math.floor(Math.random() * 1e16).toString(16).padStart(13, "0");
}

function readCookie(name) {
  try {
    const m = document.cookie.match(new RegExp("(?:^|;\\s*)" + name + "=([^;]+)"));
    return m ? decodeURIComponent(m[1]) : null;
  } catch (e) {
    return null;
  }
}

export function getOrCreateFbp() {
  if (typeof window === "undefined") return null;
  // Prefer the real `_fbp` cookie set by the browser pixel so browser + CAPI
  // events carry the SAME fbp (best match quality / identity consistency).
  const cookieFbp = readCookie("_fbp");
  if (cookieFbp) {
    localStorage.setItem(FBP_KEY, cookieFbp);
    return cookieFbp;
  }
  let v = localStorage.getItem(FBP_KEY);
  if (!v) {
    const ts = Math.floor(Date.now() / 1000);
    v = `fb.1.${ts}.${rand16()}`;
    localStorage.setItem(FBP_KEY, v);
  }
  return v;
}

const FBC_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000; // Meta: fbclid expires after 90 days

const fbcAgeOk = (fbc) => {
  const ts = Number((fbc || "").split(".")[2]);
  return Number.isFinite(ts) && Date.now() - ts < FBC_MAX_AGE_MS;
};

export function getOrCreateFbc() {
  if (typeof window === "undefined") return null;
  // fbc format: `fb.{subdomainIndex}.{creationTimeMs}.{fbclid}` (creationTime in ms, first-observed).
  // Rules: a NEW fbclid in the URL always replaces the stored one; a stored fbc older
  // than 90 days is discarded (Meta flags "expired fbclid"); otherwise never modify it.
  try {
    const url = new URL(window.location.href);
    const fbclid = url.searchParams.get("fbclid");
    const stored = localStorage.getItem(FBC_KEY);
    if (fbclid) {
      if (stored && stored.endsWith(`.${fbclid}`) && fbcAgeOk(stored)) return stored; // same click, keep creationTime
      const v = `fb.1.${Date.now()}.${fbclid}`;
      localStorage.setItem(FBC_KEY, v);
      return v;
    }
    if (stored) {
      if (fbcAgeOk(stored)) return stored;
      localStorage.removeItem(FBC_KEY); // expired
    }
    // No fbclid in URL — fall back to the `_fbc` cookie the pixel may have set
    const cookieFbc = readCookie("_fbc");
    if (cookieFbc && fbcAgeOk(cookieFbc)) {
      localStorage.setItem(FBC_KEY, cookieFbc);
      return cookieFbc;
    }
  } catch (e) {
    devWarn("[pixel] fbc parse failed", e);
  }
  return null;
}

export function getOrCreateExternalId() {
  // Persistent fallback external_id; will be overridden by phone-derived id when available
  let v = localStorage.getItem(EXTID_KEY);
  if (!v) {
    v = `m2s_${Date.now().toString(36)}_${rand16()}`;
    localStorage.setItem(EXTID_KEY, v);
  }
  return v;
}

// Persistent PII (stored after order) - for returning visitor EMQ boost
export function savePII(pii) {
  try {
    const data = { ...pii, _ts: Math.floor(Date.now() / 1000) };
    localStorage.setItem(PII_KEY, JSON.stringify(data));
  } catch (e) {
    devWarn("[pixel] savePII failed", e);
  }
}

export function getPII() {
  try {
    const raw = JSON.parse(localStorage.getItem(PII_KEY) || "null");
    if (!raw) return null;
    // 180-day expiry
    const ageDays = (Math.floor(Date.now() / 1000) - (raw._ts || 0)) / 86400;
    if (ageDays > 180) {
      localStorage.removeItem(PII_KEY);
      return null;
    }
    return raw;
  } catch (e) {
    return null;
  }
}

export function makeEventId(prefix = "evt") {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/* ---------- Client IP (IPv6-preferred) ----------
   Meta CAPI quality flags an IPv4/IPv6 mismatch when the browser pixel sees
   the user on IPv6 but our server (behind K8s/CDN proxy chain) only forwards
   IPv4 in X-Forwarded-For. Fix: have the browser fetch its own public IP from
   ipify (which returns IPv6 if the user has it) and forward it in the CAPI
   body. The backend prefers this when present.                              */
let _ipPromise = null;
let _ipMem = null;

export function getCachedClientIp() {
  // Sync accessor — for sendBeacon paths where we cannot await
  if (_ipMem) return _ipMem;
  try {
    const raw = JSON.parse(localStorage.getItem(IP_KEY) || "null");
    if (raw && raw.ip && (Date.now() - (raw.ts || 0) < IP_TTL_MS)) {
      _ipMem = raw.ip;
      return _ipMem;
    }
  } catch (e) { /* ignore */ }
  return null;
}

export function fetchClientIp() {
  if (typeof window === "undefined") return Promise.resolve(null);
  // Memory cache for the rest of the session
  if (_ipPromise) return _ipPromise;
  // LocalStorage cache with 1h TTL — saves a network round-trip per pageview
  const cached = getCachedClientIp();
  if (cached) {
    _ipPromise = Promise.resolve(cached);
    return _ipPromise;
  }

  _ipPromise = fetch("https://api64.ipify.org?format=json", {
    cache: "no-store",
    referrerPolicy: "no-referrer",
    // Short timeout so we never block tracking calls for long
    signal: (typeof AbortSignal !== "undefined" && AbortSignal.timeout) ? AbortSignal.timeout(1500) : undefined,
  })
    .then((r) => (r && r.ok ? r.json() : null))
    .then((j) => {
      const ip = j && j.ip ? j.ip : null;
      if (ip) {
        _ipMem = ip;
        try { localStorage.setItem(IP_KEY, JSON.stringify({ ip, ts: Date.now() })); } catch (e) { /* ignore */ }
      }
      return ip;
    })
    .catch(() => null);
  return _ipPromise;
}

/* ---------- Wait briefly for `_fbc` cookie ----------
   The Meta browser pixel sets `_fbc` from `fbclid` AFTER fbevents.js loads.
   Our `fireCapi` can run before that on first paint, so CAPI would send an
   empty fbc while the browser pixel sends a populated one — Meta flags this
   as low EMQ. Poll the cookie up to `maxMs` so CAPI catches up.             */
async function waitForFbc(maxMs = 600) {
  let fbc = getOrCreateFbc();
  if (fbc) return fbc;
  const start = Date.now();
  while (Date.now() - start < maxMs) {
    await new Promise((r) => setTimeout(r, 100));
    fbc = getOrCreateFbc();
    if (fbc) return fbc;
  }
  return null;
}

/* ---------- Browser Pixel (fbq) with never-drop guard ----------
   The inline fbq stub in index.html queues calls until fbevents.js loads,
   so events are normally never dropped. This extra guard also covers the
   rare case where the inline snippet itself hasn't executed yet. */
function fbqSafe(...args) {
  if (typeof window === "undefined") return;
  if (typeof window.fbq === "function") {
    window.fbq(...args);
    return;
  }
  // fbq stub not ready yet — queue and flush once it appears
  window._m2sFbqQueue = window._m2sFbqQueue || [];
  window._m2sFbqQueue.push(args);
  if (!window._m2sFbqPoll) {
    window._m2sFbqPoll = setInterval(() => {
      if (typeof window.fbq === "function") {
        clearInterval(window._m2sFbqPoll);
        window._m2sFbqPoll = null;
        const q = window._m2sFbqQueue.splice(0);
        q.forEach((a) => window.fbq(...a));
      }
    }, 200);
  }
}

export function firePixel(eventName, customData = {}, eventId) {
  try {
    if (SERVER_ONLY_EVENTS.includes(eventName)) return; // Purchase = CAPI only
    const cd = {};
    for (const k of PIXEL_DATA_KEYS) {
      if (customData[k] !== undefined) cd[k] = customData[k];
    }
    fbqSafe("track", eventName, cd, eventId ? { eventID: eventId } : undefined);
  } catch (e) {
    devWarn("[pixel] fbq failed", e);
  }
}

export async function fireCapi(eventName, opts = {}) {
  if (typeof window === "undefined") return;
  try {
    // ONE shared event_id → browser pixel + CAPI → Meta deduplicates
    const eventId = opts.event_id || makeEventId(eventName.toLowerCase());
    const customData = opts.custom_data || {};

    // 1) Browser pixel (skipped automatically for server-only events like Purchase)
    firePixel(eventName, customData, eventId);

    // 2) Resolve fbc + client_ip in parallel (each capped to keep CAPI snappy)
    const [fbc, clientIp] = await Promise.all([
      waitForFbc(800),
      Promise.race([
        fetchClientIp(),
        // Cap at 2s — ipify can take 0.7–1.5s on cold connect; after that
        // it's cached for 1h so all subsequent events resolve instantly.
        new Promise((r) => setTimeout(() => r(null), 2000)),
      ]),
    ]);

    // 3) Server-side CAPI
    const pii = getPII();
    const body = {
      event_name: eventName,
      event_id: eventId,
      event_time: Math.floor(Date.now() / 1000),
      event_source_url: window.location.href,
      referrer_url: document.referrer || undefined,
      user_agent: navigator.userAgent,
      fbp: getOrCreateFbp(),
      fbc: fbc,
      client_ip: clientIp,        // IPv6-preferred IP forwarded to backend
      external_id: opts.external_id || getOrCreateExternalId(),
      user_data: { ...(pii || {}), ...(opts.user_data || {}) },
      custom_data: { utm: getUTM(), ...customData },
    };
    await fetch(`${API}/meta/capi`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch (e) {
    // never block UI
    devWarn("capi failed", e);
  }
}
